import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { canonicalizeTaskNumber } from '@internal/spassu-code-contract';
import { makeStyles } from '@material-ui/core';
import { ExecutionState, ReviewResult, TaskItem } from '../../api/types';
import { TaskAccordionItem } from './TaskAccordionItem';
import { EmptyTaskState, ExecutionHeader } from './TaskExecutionHeader';

export interface ActiveTaskSlot {
  content: string;
  slot: number;
  totalSlots: number;
}

export interface TaskListPanelProps {
  tasks: TaskItem[];
  executionState: ExecutionState;
  onStartExecution: () => void;
  onPause: () => void;
  onResume: () => void;
  onRetry: (taskNumber: string) => void;
  isStreaming: boolean;
  streamingTaskNumber: string | null;
  streamingContent: string;
  reviewResult?: ReviewResult;
  isReviewLoading?: boolean;
  /** Parallel execution: map of taskNumber -> slot info with streaming content */
  activeTaskSlots?: Record<string, ActiveTaskSlot>;
  workerConnected?: boolean;
  nowMs?: number;
}

export type TaskDisplayStatus =
  | 'pending'
  | 'waiting'
  | 'running'
  | 'completed'
  | 'integrated'
  | 'error'
  | 'blocked'
  | 'paused';

const STATUS_CONFIG: Record<
  TaskDisplayStatus,
  {
    label: string;
    className: string;
  }
> = {
  pending: { label: 'Pendente', className: 'chipPending' },
  waiting: {
    label: 'Aguardando dep.',
    className: 'chipPaused',
  },
  running: { label: 'Rodando', className: 'chipRunning' },
  completed: {
    label: 'Concluída',
    className: 'chipCompleted',
  },
  integrated: {
    label: 'Integrada',
    className: 'chipCompleted',
  },
  error: { label: 'Erro', className: 'chipError' },
  blocked: { label: 'Bloqueada', className: 'chipError' },
  paused: { label: 'Pausada', className: 'chipPaused' },
};

const SECONDS_PER_MINUTE = 60;
const MS_PER_SECOND = 1000;
const MINUTE_MS = SECONDS_PER_MINUTE * MS_PER_SECOND;
const STALE_TASK_MINUTES = 4;
const STALE_TASK_MS = STALE_TASK_MINUTES * MINUTE_MS;
const LOG_SCROLL_THRESHOLD_PX = 80;

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 'clamp(340px, 56vh, 760px)',
  },
  header: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  progressBar: {
    marginTop: theme.spacing(1),
  },
  controls: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1.5),
  },
  startButton: {
    backgroundColor: theme.palette.secondary.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  taskList: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(1.5, 2),
  },
  accordion: {
    marginBottom: theme.spacing(1),
    '&:before': {
      display: 'none',
    },
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      gap: theme.spacing(1.5),
    },
  },
  taskNumber: {
    fontWeight: 600,
    minWidth: 40,
  },
  taskTitle: {
    flex: 1,
  },
  chipPending: {
    backgroundColor: theme.palette.grey[400],
    color: '#fff',
  },
  chipRunning: {
    backgroundColor: theme.palette.info.main,
    color: '#fff',
  },
  chipCompleted: {
    backgroundColor: theme.palette.success.main,
    color: '#fff',
  },
  chipError: {
    backgroundColor: theme.palette.error.main,
    color: '#fff',
  },
  chipPaused: {
    backgroundColor: theme.palette.warning.main,
    color: '#fff',
  },
  lastUpdateText: {
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  slotChip: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
    marginLeft: theme.spacing(0.5),
    fontSize: '0.7rem',
    height: 20,
  },
  logsContainer: {
    maxHeight: 300,
    overflowY: 'auto',
    backgroundColor: theme.palette.grey[900],
    color: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    width: '100%',
  },
  logsText: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  retryButton: {
    marginTop: theme.spacing(1),
  },
  errorMessage: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
    fontWeight: 500,
  },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    padding: theme.spacing(4),
  },
}));

export type TaskClasses = ReturnType<typeof useStyles>;

export interface TaskViewModel {
  task: TaskItem;
  displayStatus: TaskDisplayStatus;
  stateDescription: string;
  dependsOnRefs: string[];
  unlocksRefs: string[];
  log: string;
  lastUpdatedLabel: string | null;
  slotInfo?: ActiveTaskSlot;
  isActiveStreaming: boolean;
  staleMinutes: number | null;
}

interface TaskPanelViewState {
  effectiveExecutionState: ExecutionState;
  completedCount: number;
  runningCount: number;
  totalCount: number;
  progressPercent: number;
  slotSummary: string | null;
  viewModels: TaskViewModel[];
  logsContainerRef: RefObject<HTMLDivElement>;
}

function normalizeTaskRefs(refs?: string[]): string[] {
  return Array.from(
    new Set((refs ?? []).map(ref => canonicalizeTaskNumber(ref))),
  );
}

function buildTaskByNumber(tasks: TaskItem[]): Map<string, TaskItem> {
  return new Map(
    tasks.map(task => [canonicalizeTaskNumber(task.taskNumber), task]),
  );
}

function buildUnlocksByNumber(tasks: TaskItem[]): Map<string, string[]> {
  const unlocks = new Map<string, string[]>();
  for (const task of tasks) {
    const taskNumber = canonicalizeTaskNumber(task.taskNumber);
    for (const dependency of normalizeTaskRefs(task.dependsOn)) {
      unlocks.set(dependency, [...(unlocks.get(dependency) ?? []), taskNumber]);
    }
  }
  return unlocks;
}

function isIntegratedTask(task: TaskItem): boolean {
  // RF3 (commit-worker-e-integracao-confiavel): usa APENAS o estado durável
  // `integrated`. O fallback legado (inferir integração de worktree/slot) inflava
  // "Integrada" em reload/desconexão, quando o sinal ao vivo se perdia. Sem
  // integração durável → a task é "Concluída", não "Integrada".
  return task.integrated === true;
}

function isTaskDone(task?: TaskItem): boolean {
  return task?.status === 'completed';
}

function findBlockingDependency(
  task: TaskItem,
  taskByNumber: Map<string, TaskItem>,
): string | undefined {
  return normalizeTaskRefs(task.dependsOn).find(ref => {
    const dependency = taskByNumber.get(ref);
    return dependency?.status === 'error';
  });
}

function findWaitingDependency(
  task: TaskItem,
  taskByNumber: Map<string, TaskItem>,
): string | undefined {
  return normalizeTaskRefs(task.dependsOn).find(ref => {
    return !isTaskDone(taskByNumber.get(ref));
  });
}

function getDisplayStatus(
  task: TaskItem,
  taskByNumber: Map<string, TaskItem>,
): TaskDisplayStatus {
  if (task.status === 'pending' && findBlockingDependency(task, taskByNumber)) {
    return 'blocked';
  }
  if (task.status === 'pending' && findWaitingDependency(task, taskByNumber)) {
    return 'waiting';
  }
  if (task.status === 'completed' && isIntegratedTask(task)) {
    return 'integrated';
  }
  return task.status;
}

function getSlotInfo(
  task: TaskItem,
  activeTaskSlots?: Record<string, ActiveTaskSlot>,
): ActiveTaskSlot | undefined {
  const direct = activeTaskSlots?.[task.taskNumber];
  if (direct) return direct;
  const canonical = canonicalizeTaskNumber(task.taskNumber);
  return activeTaskSlots?.[canonical];
}

function getTaskLog(
  task: TaskItem,
  slotInfo: ActiveTaskSlot | undefined,
  streamingTaskNumber: string | null,
  streamingContent: string,
): string {
  if (slotInfo?.content) return slotInfo.content;
  if (
    streamingTaskNumber &&
    canonicalizeTaskNumber(task.taskNumber) ===
      canonicalizeTaskNumber(streamingTaskNumber) &&
    streamingContent
  ) {
    return streamingContent;
  }
  return task.executionLog ?? '';
}

// RF5: usa o instante de atividade mais recente entre o heartbeat DURÁVEL do
// backend (`lastWorkerEventAt`, sobrevive à queda do feed SSE) e o carimbo ao
// vivo do reducer (`lastEventAt`, otimista). Assim um feed caído não gera falso
// "sem atividade" enquanto o worker progride (o poll de getTasks refresca o
// durável), e um worker de fato parado é detectado.
function latestActivityMs(task: TaskItem): number | null {
  const candidates = [task.lastWorkerEventAt, task.lastEventAt]
    .map(value => (value ? new Date(value).getTime() : Number.NaN))
    .filter(ms => Number.isFinite(ms));
  return candidates.length > 0 ? Math.max(...candidates) : null;
}

function getStaleMinutes(
  task: TaskItem,
  status: TaskDisplayStatus,
  nowMs: number,
): number | null {
  if (status !== 'running') return null;
  const lastActivityMs = latestActivityMs(task);
  if (lastActivityMs === null) return null;
  const elapsedMs = nowMs - lastActivityMs;
  if (elapsedMs < STALE_TASK_MS) return null;
  return Math.floor(elapsedMs / MINUTE_MS);
}

function formatLastUpdatedLabel(lastEventAt?: string | null): string | null {
  if (!lastEventAt) return null;
  const timestamp = new Date(lastEventAt);
  if (Number.isNaN(timestamp.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(timestamp);
}

function buildStateDescription(
  task: TaskItem,
  status: TaskDisplayStatus,
  taskByNumber: Map<string, TaskItem>,
  slotInfo?: ActiveTaskSlot,
): string {
  if (status === 'waiting') {
    const dependency = findWaitingDependency(task, taskByNumber);
    return `Estado: aguardando dependência #${dependency}`;
  }
  if (status === 'blocked') {
    const dependency = findBlockingDependency(task, taskByNumber);
    return `Estado: bloqueada, dependência #${dependency} falhou`;
  }
  if (status === 'running' && slotInfo) {
    return `Estado: rodando no slot ${slotInfo.slot + 1}/${
      slotInfo.totalSlots
    }`;
  }
  return `Estado: ${STATUS_CONFIG[status].label.toLowerCase()}`;
}

function buildTaskViewModel(args: {
  task: TaskItem;
  taskByNumber: Map<string, TaskItem>;
  unlocksByNumber: Map<string, string[]>;
  activeTaskSlots?: Record<string, ActiveTaskSlot>;
  streamingTaskNumber: string | null;
  streamingContent: string;
  isStreaming: boolean;
  nowMs: number;
}): TaskViewModel {
  const { task, taskByNumber, unlocksByNumber } = args;
  const displayStatus = getDisplayStatus(task, taskByNumber);
  const slotInfo = getSlotInfo(task, args.activeTaskSlots);
  return {
    task,
    displayStatus,
    stateDescription: buildStateDescription(
      task,
      displayStatus,
      taskByNumber,
      slotInfo,
    ),
    dependsOnRefs: normalizeTaskRefs(task.dependsOn),
    unlocksRefs:
      unlocksByNumber.get(canonicalizeTaskNumber(task.taskNumber)) ?? [],
    log: getTaskLog(
      task,
      slotInfo,
      args.streamingTaskNumber,
      args.streamingContent,
    ),
    lastUpdatedLabel: formatLastUpdatedLabel(task.lastEventAt),
    slotInfo,
    isActiveStreaming:
      Boolean(slotInfo) || isSequentialTaskStreaming(task, args),
    staleMinutes: getStaleMinutes(task, displayStatus, args.nowMs),
  };
}

function isSequentialTaskStreaming(
  task: TaskItem,
  args: { streamingTaskNumber: string | null; isStreaming: boolean },
): boolean {
  return Boolean(
    args.streamingTaskNumber &&
      args.isStreaming &&
      canonicalizeTaskNumber(task.taskNumber) ===
        canonicalizeTaskNumber(args.streamingTaskNumber),
  );
}

function useLiveNowMs(nowMs?: number): number {
  const [currentNowMs, setCurrentNowMs] = useState(() => nowMs ?? Date.now());
  useEffect(() => {
    if (nowMs !== undefined) {
      setCurrentNowMs(nowMs);
      return undefined;
    }
    const interval = window.setInterval(
      () => setCurrentNowMs(Date.now()),
      MINUTE_MS,
    );
    return () => window.clearInterval(interval);
  }, [nowMs]);
  return nowMs ?? currentNowMs;
}

function useLogAutoScroll(trigger: string) {
  const logsContainerRef = useRef<HTMLDivElement>(null);
  const scrollRafRef = useRef<number>(0);
  const scrollLogsToBottom = useCallback(() => {
    if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = requestAnimationFrame(() => {
      const el = logsContainerRef.current;
      if (!el) return;
      const isNearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight <
        LOG_SCROLL_THRESHOLD_PX;
      if (isNearBottom) el.scrollTop = el.scrollHeight;
    });
  }, []);
  useEffect(() => {
    scrollLogsToBottom();
  }, [trigger, scrollLogsToBottom]);
  useEffect(() => {
    return () => {
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    };
  }, []);
  return logsContainerRef;
}

function getSlotSummary(
  tasks: TaskItem[],
  activeTaskSlots?: Record<string, ActiveTaskSlot>,
): string | null {
  const activeSlots = Object.values(activeTaskSlots ?? {});
  const activeCount =
    activeSlots.length ||
    tasks.filter(task => task.status === 'running').length;
  const totals = [
    ...activeSlots.map(slot => slot.totalSlots),
    ...tasks.map(task => task.totalSlots ?? 0),
  ];
  const totalSlots = Math.max(0, ...totals);
  return totalSlots > 1 ? `Slots ocupados ${activeCount}/${totalSlots}` : null;
}

function useTaskPanelViewState(props: TaskListPanelProps): TaskPanelViewState {
  const { tasks, executionState, isStreaming, activeTaskSlots } = props;
  const nowMs = useLiveNowMs(props.nowMs);
  const taskByNumber = useMemo(() => buildTaskByNumber(tasks), [tasks]);
  const unlocksByNumber = useMemo(() => buildUnlocksByNumber(tasks), [tasks]);
  const scrollTrigger = Object.values(activeTaskSlots ?? {})
    .map(slot => slot.content)
    .join('\n');
  const logsContainerRef = useLogAutoScroll(
    `${props.streamingContent}${scrollTrigger}`,
  );
  const completedCount = tasks.filter(
    task => task.status === 'completed',
  ).length;
  const runningCount = tasks.filter(task => task.status === 'running').length;
  const totalCount = tasks.length;
  const progressPercent =
    totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const hasActiveSlots = Boolean(
    activeTaskSlots && Object.keys(activeTaskSlots).length > 0,
  );
  const hasRunningTask = tasks.some(task => task.status === 'running');
  const effectiveExecutionState =
    executionState === 'idle' &&
    (isStreaming || hasRunningTask || hasActiveSlots)
      ? 'running'
      : executionState;
  return {
    effectiveExecutionState,
    completedCount,
    runningCount,
    totalCount,
    progressPercent,
    slotSummary: getSlotSummary(tasks, activeTaskSlots),
    logsContainerRef,
    viewModels: tasks.map(task =>
      buildTaskViewModel({
        task,
        taskByNumber,
        unlocksByNumber,
        activeTaskSlots,
        streamingTaskNumber: props.streamingTaskNumber,
        streamingContent: props.streamingContent,
        isStreaming,
        nowMs,
      }),
    ),
  };
}

export const TaskListPanel = memo((props: TaskListPanelProps) => {
  const { tasks, isStreaming, workerConnected = true } = props;
  const classes = useStyles();
  const state = useTaskPanelViewState(props);
  const sharedControls = {
    classes,
    workerConnected,
    onStartExecution: props.onStartExecution,
    onPause: props.onPause,
    onResume: props.onResume,
  };

  if (
    tasks.length === 0 &&
    state.effectiveExecutionState === 'idle' &&
    !isStreaming
  ) {
    return <EmptyTaskState {...sharedControls} />;
  }

  return (
    <div className={classes.root}>
      <ExecutionHeader
        {...sharedControls}
        completedCount={state.completedCount}
        totalCount={state.totalCount}
        runningCount={state.runningCount}
        progressPercent={state.progressPercent}
        slotSummary={state.slotSummary}
        executionState={state.effectiveExecutionState}
      />
      <div className={classes.taskList}>
        {state.viewModels.map(view => (
          <TaskAccordionItem
            key={view.task.id}
            classes={classes}
            view={view}
            logsContainerRef={state.logsContainerRef}
            streamingTaskNumber={props.streamingTaskNumber}
            reviewResult={props.reviewResult}
            isReviewLoading={props.isReviewLoading}
            onRetry={props.onRetry}
          />
        ))}
      </div>
    </div>
  );
});
